import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import { Modal, ModalBody, Spinner } from "reactstrap";
import crossIcon from "../../../../../assets/images/cross.svg";
import StaffDefaultImage from "assets/images/staff-default-rounded.png";
import { v4 as uuidv4 } from "uuid";
import ExpandableSearchBar from "accountOwner/pages/AddEvent/SchedulingAssistant/components/ExpandableSearchBar";
import Select from "react-select";
import Text from "components/Text";
import styles from "accountOwner/pages/AddEvent/AddEvent.module.scss";
import {
  useAllActiveOffices,
  useStaffDesignation,
} from "repositories/scheduler-repository";
import Empty from "components/Empty";
import constants from "../../../../../constants";

const SelectApprover = ({
  t,
  isOpen,
  closeModal,
  staffList = [],
  handleStaffSelection,
  existingApprovers = [],
  ownerId,
  selectedApprover = null,
  hideFilters = false, // New prop to hide office and role filters
  showTitle = false, // New prop to show title/role in office policy case
}) => {
  // State management
  const [search, setSearch] = useState("");
  const [memberList, setMemberList] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);

  // Dropdown data
  const [rolesData, setRolesData] = useState([]);
  const [officeList, setOfficeList] = useState([]);

  // Selection states
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedOffice, setSelectedOffice] = useState([]);

  // Default dropdown options
  const [selectedRoleOption, setSelectedRoleOption] = useState({
    value: "",
    label: "All Roles",
  });
  const [selectedOfficeOption, setSelectedOfficeOption] = useState({
    value: "",
    label: "All Offices",
  });

  // Data fetching for roles and offices
  const { isLoading: loadingRoleData, data: staffRoleData } =
    useStaffDesignation();

  const { isLoading: officeDataLoading, data: officeData } =
    useAllActiveOffices(1, constants.ACTIVE_OFFICES_PAGE_SIZE, {
      ownerId,
      includeAdminOffice: true,
    });

  // Process office data when it's loaded
  useEffect(() => {
    if (!hideFilters && !officeDataLoading && officeData?.data?.length) {
      const formattedData = officeData.data.map((item) => ({
        label: item?.name,
        value: item?.id,
      }));
      setOfficeList([{ value: "", label: "All Offices" }, ...formattedData]);
    }
  }, [hideFilters, officeDataLoading, officeData]);

  // Also create office options from staff list for additional offices
  useEffect(() => {
    if (!hideFilters && staffList?.length) {
      const staffOffices = new Set();
      staffList.forEach((staff) => {
        if (staff.userOffice?.length) {
          staff.userOffice.forEach((userOffice) => {
            if (userOffice.office?.id && userOffice.office?.name) {
              staffOffices.add(
                JSON.stringify({
                  id: userOffice.office.id,
                  name: userOffice.office.name,
                }),
              );
            }
          });
        }
      });

      const staffOfficeOptions = Array.from(staffOffices).map((officeStr) => {
        const office = JSON.parse(officeStr);
        return {
          label: office.name,
          value: office.id,
        };
      });

      // Merge with existing office list and remove duplicates
      const existingOfficeIds = new Set(
        officeList.map((office) => office.value),
      );
      const newOffices = staffOfficeOptions.filter(
        (office) => !existingOfficeIds.has(office.value),
      );

      if (newOffices.length > 0) {
        setOfficeList((prev) => [...prev, ...newOffices]);
      }
    }
  }, [hideFilters, staffList, officeList]);

  // Process role data when it's loaded
  useEffect(() => {
    if (!hideFilters && !loadingRoleData && staffRoleData?.length) {
      const flatData = staffRoleData?.map((item) => item?.designations)?.flat();

      if (flatData?.length) {
        const formattedData = flatData.map((item) => ({
          label: item?.name,
          value: item?.id,
        }));

        setRolesData([{ value: "", label: "All Roles" }, ...formattedData]);
      }
    }
  }, [hideFilters, loadingRoleData, staffRoleData]);

  // Set initial member list when staffList changes
  useEffect(() => {
    if (staffList?.length) {
      setMemberList(staffList);
    }
  }, [staffList]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearch("");

      // If selectedApprover is provided, use it
      if (selectedApprover) {
        setSelectedStaff(selectedApprover);
      } else if (existingApprovers?.length && staffList?.length) {
        // Fallback: try to find a manual approver that matches a staff member
        const manualApprover = existingApprovers.find(
          (approver) => approver.selectionMethod === "manual" && approver.name,
        );

        if (manualApprover) {
          const approverName = manualApprover.name?.trim().toLowerCase();
          const approverNameWithoutTitle = approverName
            .replace(/\s*\([^)]*\)$/, "")
            .trim();

          const matchingStaff = staffList.find((staff) => {
            const staffFullName = `${staff.firstName || ""} ${
              staff.lastName || ""
            }`
              .trim()
              .toLowerCase();
            const staffFirstName = (staff.firstName || "").trim().toLowerCase();

            return (
              staffFullName === approverName ||
              staffFullName === approverNameWithoutTitle ||
              staffFirstName === approverName ||
              staffFirstName === approverNameWithoutTitle
            );
          });

          if (matchingStaff) {
            setSelectedStaff(matchingStaff);
          } else {
            setSelectedStaff(null);
          }
        } else {
          setSelectedStaff(null);
        }
      } else {
        setSelectedStaff(null);
      }

      // Reset filters only if not hidden
      if (!hideFilters) {
        setSelectedRoleOption({ value: "", label: "All Roles" });
        setSelectedOfficeOption({ value: "", label: "All Offices" });
        setSelectedRoles([]);
        setSelectedOffice([]);
      }
    }
  }, [isOpen, selectedApprover, hideFilters, existingApprovers, staffList]);

  const handleOfficeChange = (selected) => {
    setSelectedOfficeOption(selected);
    setSelectedOffice(selected?.value ? [selected.value] : []);
  };

  const handleRoleChange = (selected) => {
    setSelectedRoleOption(selected);
    setSelectedRoles(selected?.value ? [selected.value] : []);
  };

  const filterMembers = () => {
    let filteredList = [...staffList];

    // Skip office and role filtering if hideFilters is true
    if (!hideFilters) {
      // Apply office filter
      if (selectedOffice.length > 0) {
        filteredList = filteredList.filter((staff) => {
          // Check if staff has userOffice array and any office matches the selected office
          if (staff.userOffice?.length) {
            return staff.userOffice.some(
              (userOffice) => userOffice.office?.id === selectedOffice[0],
            );
          }
          return false;
        });
      }

      // Apply role filter
      if (selectedRoles.length > 0) {
        filteredList = filteredList.filter(
          (staff) => staff.designation?.id === selectedRoles[0],
        );
      }
    }

    // Apply search filter (always apply search filter)
    if (search.trim()) {
      const trimmedSearchValue = search.replace(/\s+/g, "").toLowerCase();
      filteredList = filteredList.filter((item) => {
        const fullName = `${item?.firstName}${item?.lastName}`.toLowerCase();
        return fullName.includes(trimmedSearchValue);
      });
    }

    setMemberList(filteredList);
  };

  const onSearch = (val) => {
    setSearch(val);
  };

  // Apply filters when search, office, or role changes
  useEffect(() => {
    filterMembers();
  }, [search, selectedOffice, selectedRoles, staffList]);

  // Check if staff member is already selected as approver
  const isAlreadyApprover = (member) => {
    return existingApprovers.some((approver) => {
      if (approver.selectionMethod === "staff") {
        return approver.staffId === member.id;
      } else if (approver.selectionMethod === "manual") {
        const memberName =
          `${member.firstName} ${member.lastName}`.toLowerCase();
        const approverName = approver.name?.toLowerCase();
        return memberName === approverName;
      }
      return false;
    });
  };

  // Check if staff member has same name as existing approver (but not the same person)
  const hasSameNameAsApprover = (member) => {
    const memberName = `${member.firstName} ${member.lastName}`.toLowerCase();
    return existingApprovers.some((approver) => {
      if (approver.selectionMethod === "manual") {
        return approver.name?.toLowerCase() === memberName;
      }
      return false;
    });
  };

  const shouldDisableMember = (member) => {
    return isAlreadyApprover(member) || hasSameNameAsApprover(member);
  };

  const isAlreadySelected = (member) => {
    return isAlreadyApprover(member);
  };

  const handleSelectUser = (staffMember) => {
    if (shouldDisableMember(staffMember)) return;
    setSelectedStaff(staffMember);
  };

  const handleApply = () => {
    if (selectedStaff) {
      handleStaffSelection(selectedStaff);
      closeModal();
    }
  };

  const handleCancel = () => {
    setSelectedStaff(null);
    closeModal();
  };

  const searchComp = (
    <div className={`search-box w-100 ${styles["search-box"]}`}>
      <ExpandableSearchBar
        placeholder={t("accountOwner.searchByName")}
        searchValue={search}
        setSearchValue={onSearch}
        expanded={true}
      />
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      toggle={closeModal}
      className="modal-dialog-centered modal-width-660 new-group-modal"
      modalClassName="custom-modal"
    >
      <span className="close-btn" onClick={handleCancel}>
        <img src={crossIcon} alt="close" />
      </span>

      <ModalBody>
        {/* Header */}
        <Text size="25px" marginBottom="10px" weight="500" color="#111b45">
          {t("officePolicyManagement.selectAprrover")}
        </Text>

        {/* Search bar - above filters */}
        <div style={{ marginBottom: "20px" }}>{searchComp}</div>

        {/* Filter dropdowns - only show if not hidden */}
        {!hideFilters && (
          <div
            className={styles.select_box_wrapper}
            style={{ marginBottom: "20px" }}
          >
            <Select
              className="react-select-container w-50 add-spacing"
              classNamePrefix="react-select"
              options={officeList}
              value={selectedOfficeOption}
              onChange={handleOfficeChange}
              isSearchable={false}
            />
            <Select
              className="react-select-container w-50 add-spacing"
              classNamePrefix="react-select"
              options={rolesData}
              value={selectedRoleOption}
              onChange={handleRoleChange}
              isSearchable={false}
            />
          </div>
        )}

        {/* Member list */}
        <ul
          className="modal-employee-list group-list"
          id="scrollableDiv"
          style={{ maxHeight: "300px", overflowY: "auto" }}
        >
          {memberList?.length > 0 ? (
            memberList.map((item, i) => {
              const disabled = shouldDisableMember(item);
              const alreadySelected = isAlreadySelected(item);
              // Check by ID first, then by name if ID doesn't match (for manual approvers)
              const checkedById =
                selectedStaff?.id && item.id && selectedStaff.id === item.id;

              // Name-based matching with multiple strategies
              let checkedByName = false;
              if (selectedStaff?.firstName && item.firstName) {
                const selectedFullName = `${selectedStaff.firstName || ""} ${
                  selectedStaff.lastName || ""
                }`
                  .trim()
                  .toLowerCase();
                const itemFullName = `${item.firstName || ""} ${
                  item.lastName || ""
                }`
                  .trim()
                  .toLowerCase();
                const selectedFirstName = (selectedStaff.firstName || "")
                  .trim()
                  .toLowerCase();
                const itemFirstName = (item.firstName || "")
                  .trim()
                  .toLowerCase();

                // Match by full name or first name
                checkedByName =
                  selectedFullName === itemFullName ||
                  selectedFirstName === itemFirstName;
              }

              const checked = (checkedById || checkedByName) && !disabled;

              return (
                <li key={uuidv4()}>
                  <div
                    className={`${styles.border_list} ch-radio ${
                      disabled ? styles.disabled_list : ""
                    }`}
                    style={{
                      padding: "12px 0",
                      borderBottom: "1px solid #e0e0e0",
                    }}
                  >
                    <label>
                      <input
                        type="radio"
                        name="selectApprover"
                        checked={checked}
                        onChange={() => !disabled && handleSelectUser(item)}
                        disabled={disabled}
                      />
                      <span className="d-flex align-items-center">
                        <img
                          src={item?.profilePic || StaffDefaultImage}
                          alt="profile-pic"
                          className={styles.Default_staff_image}
                          style={{
                            marginRight: "12px",
                            width: "40px",
                            height: "40px",
                            borderRadius: "50%",
                          }}
                        />
                        <div>
                          <div style={{ fontWeight: "600", color: "#102C42" }}>
                            {`${item?.firstName || ""} ${item?.lastName || ""}`}
                          </div>
                          {showTitle && (
                            <Text size="12px" weight="400" color="#87928D">
                              {`${item?.title ? item.title : ""}`}
                            </Text>
                          )}
                        </div>
                      </span>
                    </label>
                  </div>
                </li>
              );
            })
          ) : (
            <li>
              <Empty Message={t("noDataAvaliableForSelectoion")} />
            </li>
          )}
        </ul>

        {/* Footer buttons */}
        <button
          className="button button-round button-shadow mr-sm-3 mb-3 w-sm-100 mt-4"
          title={t("notices.select")}
          onClick={handleApply}
          disabled={!selectedStaff}
        >
          {t("notices.select")}
        </button>
        <button
          className="button button-round button-border btn-mobile-link button-dark mb-md-3 mt-md-4"
          onClick={handleCancel}
          title={t("cancel")}
        >
          {t("cancel")}
        </button>
      </ModalBody>
    </Modal>
  );
};

export default withTranslation()(SelectApprover);
